import React, { useState, useMemo } from 'react';
import { THEME_CONFIG } from './ThemedSections';
import { specifyCatLabels } from '../constants/translations';
import { SwapCandidateThumb } from './SwapCandidateThumb';
import { localName } from './AttractionCard';
import { FavHeartButton } from './FavHeartButton';
import { byRankScore } from '../utils/rank';

const CATEGORY_ORDER = ['culture', 'nature', 'play', 'shopping', 'food'];

const STRINGS = {
  'zh-HK': { pick: '選擇', detail: '詳情', empty: '此分類暫無景點', pickCategory: '請先選擇一個類別', allDistricts: '全部地區' },
  'zh-CN': { pick: '选择', detail: '详情', empty: '此分类暂无景点', pickCategory: '请先选择一个类别', allDistricts: '全部地区' },
  en:      { pick: 'Pick', detail: 'Details', empty: 'No venues in this category', pickCategory: 'Choose a category to start', allDistricts: 'All areas' },
  ja:      { pick: '選択', detail: '詳細', empty: 'このカテゴリにはスポットがありません', pickCategory: 'カテゴリを選んでください', allDistricts: 'すべてのエリア' },
  ko:      { pick: '선택', detail: '상세', empty: '이 카테고리에는 명소가 없습니다', pickCategory: '카테고리를 먼저 선택하세요', allDistricts: '전체 지역' },
};
const getStrings = (lang) => STRINGS[lang] || STRINGS['zh-HK'];

// Inline (non-modal) category browser — renders in the same spot the
// distance-based "換一批" batch already occupies, so picking a category
// doesn't jump the traveller into a separate full-screen flow. Always shows
// every match in the chosen (sub)category, sorted by rating, no distance
// cutoff — this is what still works even for a slot with no real
// coordinates at all (see swapCandidates' fallback).
export function ActivityPicker({ attractions, lang, onPick, onDetail, favorites, onToggleFav }) {
  const [category, setCategory] = useState(null);
  const [specifyCat, setSpecifyCat] = useState(null);
  const [district, setDistrict] = useState(null);
  const s = getStrings(lang);
  const langKey = lang === 'zh-CN' ? 'zhCN' : lang === 'en' ? 'en' : lang === 'ja' ? 'ja' : lang === 'ko' ? 'ko' : 'zh';

  // District chips are data-driven (whatever raw `district` values this
  // city's pool actually has) rather than the static CITY_DISTRICTS list —
  // that list is keyed by English city name, which this component doesn't
  // reliably have, and a data-driven set never offers an empty chip.
  const districtOptions = useMemo(() => {
    const counts = new Map();
    for (const a of attractions) {
      if (!a.district) continue;
      counts.set(a.district, (counts.get(a.district) || 0) + 1);
    }
    // Cap to the top 20 by frequency — a city like Hong Kong has 3,000+ rows
    // and dozens of distinct raw district strings; an unbounded chip row
    // would scroll forever and bury the areas travellers actually visit.
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 20).map(([d]) => d);
  }, [attractions]);

  const catAttractions = useMemo(
    () => category
      ? attractions.filter(a => a.category === category && (!district || a.district === district))
      : [],
    [attractions, category, district]
  );
  const specifyOptions = useMemo(
    () => [...new Set(catAttractions.filter(a => a.specify_cat).map(a => a.specify_cat))].sort(),
    [catAttractions]
  );
  const shown = useMemo(() => {
    const list = specifyCat ? catAttractions.filter(a => a.specify_cat === specifyCat) : catAttractions;
    return list.slice().sort(byRankScore).slice(0, 20);
  }, [catAttractions, specifyCat]);

  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 10, marginTop: 4 }}>
      {/* District chips — independent of category, so a traveller can narrow
          to one area of the city before (or instead of) picking a category. */}
      {districtOptions.length > 1 && (
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4 }}>
          <button onClick={() => setDistrict(null)}
            style={{ flexShrink: 0, padding: '5px 10px', borderRadius: 20, border: '1.5px solid', cursor: 'pointer', fontSize: 10, fontWeight: 800,
              borderColor: !district ? '#0f172a' : '#e2e8f0', background: !district ? '#0f172a' : 'white', color: !district ? 'white' : '#94a3b8' }}>
            {s.allDistricts}
          </button>
          {districtOptions.map(d => {
            const active = district === d;
            return (
              <button key={d} onClick={() => setDistrict(active ? null : d)}
                style={{ flexShrink: 0, padding: '5px 10px', borderRadius: 20, border: '1.5px solid', cursor: 'pointer', fontSize: 10, fontWeight: 800,
                  borderColor: active ? '#0f172a' : '#e2e8f0', background: active ? '#0f172a' : 'white', color: active ? 'white' : '#64748b' }}>
                {d}
              </button>
            );
          })}
        </div>
      )}

      {/* Top-level category chips */}
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4 }}>
        {CATEGORY_ORDER.map(catKey => {
          const cfg = THEME_CONFIG[catKey];
          const active = category === catKey;
          return (
            <button key={catKey} onClick={() => { setCategory(catKey); setSpecifyCat(null); }}
              style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 5, padding: '6px 11px', borderRadius: 20, border: '1.5px solid', cursor: 'pointer', fontSize: 11, fontWeight: 800,
                borderColor: active ? cfg.color : '#e2e8f0', background: active ? cfg.color : 'white', color: active ? 'white' : '#475569' }}>
              <span>{cfg.icon}</span>{cfg.labels[langKey] || cfg.labels.zh}
            </button>
          );
        })}
      </div>

      {/* specify_cat sub-chips — deliberately no "all" chip; picking a category already shows everything in it */}
      {category && specifyOptions.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
          {specifyOptions.map(sc => {
            const active = specifyCat === sc;
            const cfg = THEME_CONFIG[category];
            return (
              <button key={sc} onClick={() => setSpecifyCat(active ? null : sc)}
                style={{ padding: '4px 10px', borderRadius: 20, border: '1.5px solid', fontSize: 10, fontWeight: 800, cursor: 'pointer',
                  borderColor: active ? cfg.color : '#e2e8f0', background: active ? `${cfg.color}18` : 'white', color: active ? cfg.color : '#64748b' }}>
                {(specifyCatLabels[sc] && specifyCatLabels[sc][lang]) || sc}
              </button>
            );
          })}
        </div>
      )}

      {/* Results — same photo-tile card style as the 換一批 batch row */}
      <div style={{ maxHeight: 320, overflowY: 'auto', marginTop: 8 }}>
        {!category && <p style={{ textAlign: 'center', color: '#94a3b8', fontSize: 11, padding: '14px 0', margin: 0 }}>{s.pickCategory}</p>}
        {category && shown.length === 0 && <p style={{ textAlign: 'center', color: '#94a3b8', fontSize: 11, padding: '14px 0', margin: 0 }}>{s.empty}</p>}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(112px, 1fr))', gap: 10 }}>
          {shown.map((a, i) => (
            <div key={i} style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, padding: 5 }}>
              <div style={{ width: '100%', aspectRatio: '1 / 1', borderRadius: 7, overflow: 'hidden', background: '#f1f5f9', position: 'relative' }}>
                <SwapCandidateThumb name={a.name} city={a.city} />
                <FavHeartButton name={a.name} cityKey={a.city} favorites={favorites} onToggleFav={onToggleFav} />
                {a.rating && (
                  <span style={{ position: 'absolute', bottom: 3, right: 3, background: 'rgba(15,23,42,0.75)', color: '#fbbf24', fontSize: 10, fontWeight: 800, borderRadius: 5, padding: '2px 5px' }}>
                    ★{Number(a.rating).toFixed(1)}
                  </span>
                )}
              </div>
              <p title={localName(a, lang)} style={{ fontSize: 11, fontWeight: 700, color: '#475569', margin: '6px 0 6px', lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {localName(a, lang)}
              </p>
              <div style={{ display: 'flex', gap: 4 }}>
                {onDetail && (
                  <button onClick={() => onDetail(a)}
                    style={{ flex: 1, fontSize: 11, fontWeight: 700, border: 'none', background: '#f0fdf4', color: '#15803d', borderRadius: 6, padding: '8px 0', minHeight: 32, cursor: 'pointer' }}>
                    {s.detail}
                  </button>
                )}
                <button onClick={() => onPick(a)}
                  style={{ flex: 1, fontSize: 11, fontWeight: 700, border: 'none', background: '#eff6ff', color: '#1677ff', borderRadius: 6, padding: '8px 0', minHeight: 32, cursor: 'pointer' }}>
                  {s.pick}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
